/**
 * History service
 */

/**
 * historySvc service <br>
 * Work with navigation history
 *
 * @class historySvc
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc service
 * @name mwApp/common.service:historySvc
 */
/* @ngInject */
export default function HistorySvc ($window, $location) {
  /* === private === */

  /**
   * Browser history API
   *
   * @type {History}
   */
  var history = $window.history

  /**
   * Max length of history states array
   * @type {Number}
   */
  var MAX_LENGTH = 10

  /**
   * @typedef {Object} HistoryState
   * @property {String} name - state name
   * @property {String} url  - state url
   * @property {Object} obj  - state object
   * @memberof mwApp/common.service:historySvc
   * @description Describes history state object, it is possible to add other fields in the future
   */

  /**
   * History states
   *
   * @private
   * @memberof mwApp/common.service:historySvc~
   * @type {mwApp/common.service:historySvc.HistoryState[]}
   */
  var states = []

  /* === public === */

  // native browser's history API
  this.back = back
  this.forward = forward
  this.go = go
  this.length = length
  this.pushState = pushState
  this.replaceState = replaceState

  // my history API
  this.push = push
  this.canPop = canPop
  this.pop = pop

  /* === implementation === */

  /**
   * Move backward through history, using native browser's history API
   *
   * @memberof mwApp/common.service:historySvc#
   */
  function back () {
    states.pop()
    const url = states[states.length - 1] ? states[states.length - 1].url : ''
    const urlArr = url.split('/')
    if (urlArr[0] === '') {
      urlArr.shift()
    }
    urlArr.shift()
    return $location.url(urlArr.join('/'))
  }

  /**
   * Move forward through history, using native browser's history API
   *
   * @memberof mwApp/common.service:historySvc#
   */
  function forward () {
    return history.forward()
  }

  /**
   * Moving to a specific point in history, using native browser's history API
   *
   * @memberof mwApp/common.service:historySvc#
   * @param  {Number} n - relative position to the current page
   */
  function go (n) {
    return history.go(n)
  }

  /**
   * History length, using native browser's history API
   *
   * @memberof mwApp/common.service:historySvc#
   */
  function length () {
    return history.length
  }

  /**
   * Add history entry, using native browser's history API
   *
   * @memberof mwApp/common.service:historySvc#
   * @param  {Object} obj   - object which is associated with the new history entry
   * @param  {String} title - title for new history entry
   * @param  {String} url   - new history entry's URL
   */
  function pushState (obj, title, url) {
    title = title || ''
    push(obj, title, url)
    return history.pushState(obj, title, url)
  }

  /**
   * Modify history entry, using native browser's history API <br>
   * replaceState() operates exactly like pushState()
   * except that replaceState() modifies the current history entry instead of creating a new one
   *
   * @memberof mwApp/common.service:historySvc#
   * @param  {Object} obj   - object which is associated with the new history entry
   * @param  {String} title - title for new history entry
   * @param  {String} url   - new history entry's URL
   */
  function replaceState (obj, title, url) {
    title = title || ''
    push(obj, title, url)
    return history.replaceState(obj, title, url)
  }

  /**
   * Push history entry to states
   *
   * @memberof mwApp/common.service:historySvc#
   * @param  {Object} obj  - object which is associated with the new history entry
   * @param  {String} name - title for new history entry
   * @param  {String} url  - new history entry's URL
   */
  function push (obj, name, url) {
    url = url || '/nora' + $location.url()
    if (states[states.length - 1]) {
      const prevSplitted = states[states.length - 1].url.split('/')
      const prevId = prevSplitted.pop()
      const curSplitted = url.split('/')
      curSplitted.pop()

      if (prevSplitted.join('/') === curSplitted.join('/') && prevId === 'new') {
        states[states.length - 1] = {
          name: name,
          url: url,
          obj: obj
        }
        return
      }
    }
    if (url && states.length && url === states[states.length - 1].url) {
      return
    }
    if (url && states.length && url.split('#')[0] === states[states.length - 1].url.split('#')[0]) {
      states[states.length - 1] = {
        name: name,
        url: url,
        obj: obj
      }
      return
    }
    states.push({
      name: name,
      url: url,
      obj: obj
    })
    if (states.length > MAX_LENGTH) {
      states.shift() // remove first element from array
    }
  }

  /**
   * Check, is it possible to pop history entry from states
   *
   * @memberof mwApp/common.service:historySvc#
   * @param  {String} name - title of history entry
   */
  function canPop (name) {
    if (states.length < 3) {
      return false
    }

    if (name === undefined) {
      return true
    }

    for (var i = states.length; i--;) {
      if (states[i].name === name) {
        return true
      }
    }

    return false
  }

  /**
   * Pop history entry from states
   *
   * @memberof mwApp/common.service:historySvc#
   * @param  {String} name - title of history entry
   * @param withRedirect
   */
  function pop (name, withRedirect = true) {
    var state
    if (name === undefined) {
      states.pop()
      state = states[states.length - 1]
    } else {
      for (var i = states.length; i--;) {
        if (states[i].name === name) {
          state = states.splice(i, 1)
          break
        }
      }
    }

    if (state && withRedirect) {
      $location.url(state.url)
    }
  }
}
